import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useReveal } from "../hooks/useReveal";
import PrivacyAgreement from "./PrivacyAgreement";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POUCH =
  "/assets/pouch.png";

const FIELDS = [
  { name: "first_name", label: "First name", placeholder: "First name", required: true },
  { name: "last_name", label: "Last name", placeholder: "Last name", required: true },
  { name: "email", label: "Email", placeholder: "you@locked.in", required: true, type: "email" },
  { name: "instagram", label: "Instagram", placeholder: "@yourhandle", required: false },
  { name: "occupation", label: "Occupation", placeholder: "What you do", required: true },
];

const INITIAL = {
  first_name: "",
  last_name: "",
  email: "",
  instagram: "",
  occupation: "",
  message: "",
  agreed: false,
};

export default function NotifyMe({ formRef }) {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const [errors, setErrors] = useState({});
  const [policyOpen, setPolicyOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [submittedName, setSubmittedName] = useState("");
  const ref = useReveal();

  const setField = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Required";
    if (!form.last_name.trim()) errs.last_name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Enter a valid email";
    if (!form.occupation.trim()) errs.occupation = "Required";
    if (!form.message.trim()) errs.message = "Required";
    if (!form.agreed)
      errs.agreed =
        "You must accept the Protos Private Access Agreement to join the Protos 100.";
    setErrors(errs);
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setStatus("err");
      // Prioritize the agreement message — it is mandatory
      if (errs.agreed) {
        setMsg(errs.agreed);
        toast.error("Accept the Private Access Agreement to continue.", {
          description:
            "Joining the Protos 100 requires agreeing to the terms before submitting.",
        });
        // Scroll the checkbox into view so user sees it
        document
          .querySelector('[data-testid="notify-agreed"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        setMsg("Please complete the required fields.");
        toast.error("Some required fields are missing.");
      }
      return;
    }
    setStatus("loading");
    setMsg("");
    try {
      await axios.post(`${API}/subscribe`, {
        ...form,
        source: "protos_100_application",
      });
      // Fetch the new position from the count endpoint
      let pos = null;
      try {
        const cr = await axios.get(`${API}/subscribers/count`);
        pos = parseInt(cr.data?.count ?? 0, 10) || null;
      } catch {
        /* non-blocking */
      }
      setPosition(pos);
      setSubmittedName(form.first_name);
      setStatus("ok");
      setMsg("You're on the list. We'll be in touch.");
      toast.success("Application received. We'll be in touch.");
      setForm(INITIAL);
    } catch (err) {
      setStatus("err");
      const detail = err?.response?.data?.detail || "Something broke. Try again.";
      setMsg(detail);
      toast.error(detail);
    }
  };

  return (
    <section
      id="notify"
      ref={formRef}
      data-testid="notify-section"
      className="section relative overflow-hidden"
    >
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] aspect-square rounded-full blur-3xl opacity-50"
          style={{
            background:
              "radial-gradient(circle at center, rgba(31,179,255,0.18), transparent 60%)",
          }}
        />
      </div>

      <div
        ref={ref}
        className="reveal container-x relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-16"
      >
        {/* Left: heading + pouch */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <div className="tag tag-blue">
            <span className="tag-dot" /> Application
          </div>
          <h2
            className="kinetic mt-6"
            style={{ fontSize: "clamp(2.6rem, 7vw, 6rem)", lineHeight: 0.95 }}
          >
            <span className="gradient-text">JOIN THE</span>
            <br />
            <span className="outline-text-blue">PROTOS 100.</span>
          </h2>
          <p className="mt-8 text-white/60 text-base lg:text-lg leading-relaxed max-w-md">
            Tell us a little about yourself and the moments you want to lock in. We
            review applications in waves. Selected participants get a pouch and
            access to the 14-day beta.
          </p>

          <div className="hidden lg:flex mt-12 justify-start">
            <img src={POUCH} alt="" className="float-pouch w-56 xl:w-64" />
          </div>
        </div>

        {/* Right: form OR celebration */}
        <div className="lg:col-span-7">
          {status === "ok" ? (
            <SubmittedCard
              name={submittedName}
              position={position}
              onReset={() => {
                setStatus("idle");
                setMsg("");
                setPosition(null);
              }}
            />
          ) : (
          <form
            onSubmit={onSubmit}
            className="card-dark p-6 md:p-8 lg:p-10 space-y-6"
            data-testid="notify-form"
            noValidate
          >
            <div className="grid sm:grid-cols-2 gap-5">
              {FIELDS.slice(0, 2).map((f) => (
                <FormField
                  key={f.name}
                  f={f}
                  value={form[f.name]}
                  onChange={setField(f.name)}
                  error={errors[f.name]}
                />
              ))}
            </div>
            {FIELDS.slice(2).map((f) => (
              <FormField
                key={f.name}
                f={f}
                value={form[f.name]}
                onChange={setField(f.name)}
                error={errors[f.name]}
              />
            ))}

            {/* Message */}
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.25em] text-white/55 mb-2">
                Moments that matter <span style={{ color: "var(--blue-bright)" }}>*</span>
              </label>
              <p className="text-white/45 text-xs mb-3 leading-relaxed">
                What are the moments in your life where you want to feel more
                focused, calm, present, or locked in?
              </p>
              <textarea
                data-testid="notify-input-message"
                value={form.message}
                onChange={setField("message")}
                rows={4}
                placeholder="Tell us briefly…"
                className="field !rounded-2xl !py-3.5 resize-none w-full"
              />
              {errors.message && (
                <div className="mt-2 font-mono text-xs text-red-400">{errors.message}</div>
              )}
            </div>

            {/* Agreement (mandatory) */}
            <div
              className={`rounded-2xl p-4 -m-4 transition-colors ${
                errors.agreed
                  ? "border border-red-500/60 bg-red-500/5"
                  : "border border-transparent"
              }`}
            >
              <label className="flex items-start gap-3 cursor-pointer select-none group">
                <input
                  data-testid="notify-agreed"
                  type="checkbox"
                  checked={form.agreed}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setForm((p) => ({ ...p, agreed: v }));
                    if (v) setErrors((er) => ({ ...er, agreed: undefined }));
                  }}
                  className="peer sr-only"
                />
                <span
                  className={`mt-0.5 inline-flex shrink-0 w-5 h-5 rounded-md border transition-all ${
                    form.agreed
                      ? "border-[var(--blue-bright)] bg-[rgba(31,179,255,0.18)]"
                      : errors.agreed
                        ? "border-red-400"
                        : "border-white/25 group-hover:border-white/50"
                  }`}
                >
                  {form.agreed && (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      className="w-full h-full"
                      style={{ color: "var(--blue-bright)" }}
                    >
                      <path
                        d="M5 12l4 4 10-10"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span className="text-white/65 text-xs lg:text-sm leading-relaxed">
                  By checking this box, you confirm that you understand and agree to
                  the terms of the{" "}
                  <button
                    type="button"
                    data-testid="open-privacy"
                    onClick={(e) => {
                      e.preventDefault();
                      setPolicyOpen(true);
                    }}
                    className="underline underline-offset-4 hover:text-white transition-colors"
                    style={{ color: "var(--blue-bright)" }}
                  >
                    Protos Private Access Agreement
                  </button>
                  . <span style={{ color: "var(--blue-bright)" }}>*</span>
                </span>
              </label>
              {errors.agreed && (
                <div
                  data-testid="notify-agreed-error"
                  className="mt-3 pl-8 flex items-start gap-2 font-mono text-xs text-red-400"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="mt-0.5 shrink-0">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 8v5M12 16.5v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span>{errors.agreed}</span>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
              <button
                data-testid="notify-submit"
                data-magnet
                type="submit"
                className="btn-primary justify-center sm:w-auto"
                disabled={status === "loading"}
              >
                <span>{status === "loading" ? "Sending…" : "Submit Application"}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M13 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-white/40">
                Reviewed within 5 business days
              </span>
            </div>

            <div className="h-6" aria-live="polite">
              {status === "ok" && (
                <span
                  data-testid="notify-success"
                  className="font-mono text-sm"
                  style={{ color: "var(--blue-bright)" }}
                >
                  ✓ {msg}
                </span>
              )}
              {status === "err" && (
                <span data-testid="notify-error" className="font-mono text-sm text-red-400">
                  ✕ {msg}
                </span>
              )}
            </div>
          </form>
          )}
        </div>
      </div>

      <PrivacyAgreement open={policyOpen} onClose={() => setPolicyOpen(false)} />
    </section>
  );
}

function SubmittedCard({ name, position, onReset }) {
  const positionLabel =
    position == null
      ? "Position pending"
      : position > 100
        ? `Waitlist #${position - 100}`
        : `#${String(position).padStart(2, "0")} of 100`;
  return (
    <div
      data-testid="notify-submitted"
      className="card-dark p-8 md:p-10 lg:p-12 relative overflow-hidden"
    >
      <div
        className="absolute -inset-12 -z-10 blur-3xl opacity-60 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, rgba(31,179,255,0.35), transparent 60%)",
        }}
      />

      {/* Animated check ring */}
      <div className="relative w-20 h-20 mb-8">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            border: "1px solid rgba(63,226,255,0.4)",
            animation: "ringPulse 2.4s ease-in-out infinite",
          }}
        />
        <div
          className="absolute inset-2 rounded-full flex items-center justify-center"
          style={{
            background:
              "linear-gradient(135deg, rgba(31,179,255,0.2), rgba(63,226,255,0.05))",
            border: "1px solid rgba(63,226,255,0.5)",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            className="w-8 h-8"
            style={{ color: "var(--blue-bright)" }}
          >
            <path
              d="M5 12l4 4 10-10"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeDasharray: 30,
                strokeDashoffset: 30,
                animation: "drawCheck 0.7s ease-out 0.2s forwards",
              }}
            />
          </svg>
        </div>
      </div>

      <div className="tag tag-blue mb-4">
        <span className="tag-dot" /> Application Received
      </div>

      <h3
        className="kinetic"
        style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 0.95 }}
      >
        <span className="gradient-text">YOU'RE IN LINE</span>
        {name && (
          <>
            <span className="gradient-text">, </span>
            <span className="outline-text-blue">{(name || "").toUpperCase()}.</span>
          </>
        )}
      </h3>

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <div
          className="p-5 rounded-2xl"
          style={{
            background:
              "linear-gradient(135deg, rgba(31,179,255,0.12), rgba(31,179,255,0.02))",
            border: "1px solid rgba(63,226,255,0.35)",
          }}
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55">
            Your Position
          </div>
          <div
            className="count-up mt-1"
            data-testid="submitted-position"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            {positionLabel}
          </div>
        </div>
        <div className="p-5 rounded-2xl border border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55">
            Next Step
          </div>
          <div className="mt-2 text-white/80 text-sm leading-relaxed">
            We review applications in waves. Selected applicants receive an email
            from <span style={{ color: "var(--blue-bright)" }}>protos@os.revurge.com</span> with their pouch on the way.
          </div>
        </div>
      </div>

      <ul className="mt-8 space-y-2 text-sm text-white/65">
        {[
          "Keep an eye on your inbox (and spam folder)",
          "Selected within 5 business days",
          "14-day protocol begins when your pouch arrives",
        ].map((t, i) => (
          <li key={i} className="flex items-center gap-3">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--blue-bright)" }}
            />
            {t}
          </li>
        ))}
      </ul>

      <div className="mt-10 flex flex-wrap gap-3 items-center">
        <button
          type="button"
          data-testid="notify-reset"
          onClick={onReset}
          className="btn-ghost"
        >
          Submit another
        </button>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
          Made for the moment.
        </span>
      </div>

      <style>{`
        @keyframes ringPulse {
          0%,100% { transform: scale(1); opacity: 0.55; }
          50% { transform: scale(1.15); opacity: 0; }
        }
        @keyframes drawCheck {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}

function FormField({ f, value, onChange, error }) {
  return (
    <div>
      <label className="block font-mono text-xs uppercase tracking-[0.25em] text-white/55 mb-2">
        {f.label}{" "}
        {f.required && <span style={{ color: "var(--blue-bright)" }}>*</span>}
      </label>
      <input
        data-testid={`notify-input-${f.name}`}
        type={f.type || "text"}
        value={value}
        onChange={onChange}
        placeholder={f.placeholder}
        className="field"
      />
      {error && (
        <div className="mt-2 font-mono text-xs text-red-400">{error}</div>
      )}
    </div>
  );
}
